/* eslint-disable max-lines */
import { User } from '@litlingo/client';
import { selectUsers } from 'actions/userPermissionsPage';
import MultiselectDropdownRedesign, {
  MultiSelectOption,
} from 'components/MultiSelectDropdown/MultiSelectDropdownRedesign';
import SelectRedesign from 'components/Select/SelectRedesign';
import useAllTeams from 'hooks/teams/useAllTeams';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { getSelectedUsers } from 'selectors/userPermissionsPage';
import { getAvailablesRoles } from 'selectors/users';
import userTypeMap from 'utils/usersListMap';
import UserPermissionsSelectFilter from './UserPermissionsSelectFilter';

const activeOptions = [
  { value: 'true', label: 'Has Integrations' },
  { value: 'false', label: 'No Integrations' },
  { value: 'show-all', label: 'Show All' },
];

type ComponentProps = {
  users: User[];
  setFilteredUsers: React.Dispatch<React.SetStateAction<User[]>>;
};

const UserPermissionsFilters: React.FC<ComponentProps> = ({ users, setFilteredUsers }) => {
  const dispatch = useDispatch();
  const route = useRouteMatch();

  const { data } = useAllTeams({ kind: 'division' });
  const { data: regionsData } = useAllTeams({ kind: 'region' });
  const { data: managementLevelsData } = useAllTeams({ kind: 'management-level' });
  const roles = useSelector(getAvailablesRoles);
  const selectedUsers = useSelector(getSelectedUsers);

  const userTypeName = Object.entries(userTypeMap).find(
    ([k]) => route.path.split('/').pop() === k
  )?.[0];

  const [active, setActive] = useState<string>('');
  const [selectedRoles, setSelectedRoles] = useState<{ label: string; value: string }[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<{ label: string; value: string }[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<{ label: string; value: string }[]>([]);
  const [selectedManagementLevels, setSelectedManagementLevels] = useState<
    { label: string; value: string }[]
  >([]);

  const teams = data?.records || [];
  const regions = regionsData?.records || [];

  const teamOptions = teams.map((team) => ({
    label: team.name,
    value: team.uuid,
  }));

  const rolesOptions = roles.map((key) => ({
    label: key,
    value: key,
  }));

  const regionsOptions = regions.map((team) => ({
    label: team.name,
    value: team.uuid,
  }));

  const managementLevelsOptions =
    managementLevelsData?.records.map((team) => ({
      label: team.name,
      value: team.uuid,
    })) || [];

  useEffect(() => {
    let filteredUsers = users;
    if (active && active !== 'show-all') {
      filteredUsers = filteredUsers.filter((user) => user.sync_emails?.toString() === active);
    }

    if (selectedRoles.length > 0) {
      filteredUsers = filteredUsers.filter((user) =>
        user.roles.some((r) => selectedRoles.some((sR) => sR.value === r))
      );
    }

    if (selectedTeams.length > 0) {
      filteredUsers = filteredUsers.filter((user) =>
        user.teams?.some((t) => selectedTeams.some((sT) => sT.value === t.uuid))
      );
    }

    if (selectedRegions.length > 0) {
      filteredUsers = filteredUsers.filter((user) =>
        user.teams?.some((t) => selectedRegions.some((sT) => sT.value === t.uuid))
      );
    }

    if (selectedManagementLevels.length > 0) {
      filteredUsers = filteredUsers.filter((user) =>
        user.teams?.some((t) => selectedManagementLevels.some((sT) => sT.value === t.uuid))
      );
    }

    setFilteredUsers(filteredUsers);
  }, [
    active,
    selectedRoles,
    setFilteredUsers,
    users,
    selectedTeams,
    selectedRegions,
    selectedManagementLevels,
  ]);

  const handleChangeActive = (value: string): void => {
    setActive(value);
  };

  const handleChangeRoles = (option: MultiSelectOption): void => {
    const selected = selectedRoles.some((r) => r.value === option.value);
    if (selected) {
      setSelectedRoles((rol) => rol.filter((r) => r.value !== option.value));
    } else {
      setSelectedRoles((rol) => [...rol, option]);
    }
  };

  const handleChangeTeams = (option: MultiSelectOption): void => {
    const selected = selectedTeams.some((t) => t.value === option.value);
    if (selected) {
      setSelectedTeams((team) => team.filter((t) => t.value !== option.value));
    } else {
      setSelectedTeams((team) => [...team, option]);
    }
  };

  const handleChangeRegions = (option: MultiSelectOption): void => {
    const selected = selectedRegions.some((t) => t.value === option.value);
    if (selected) {
      setSelectedRegions((team) => team.filter((t) => t.value !== option.value));
    } else {
      setSelectedRegions((team) => [...team, option]);
    }
  };

  const handleChangeManagementLevels = (option: MultiSelectOption): void => {
    const selected = selectedManagementLevels.some((t) => t.value === option.value);
    if (selected) {
      setSelectedManagementLevels((team) => team.filter((t) => t.value !== option.value));
    } else {
      setSelectedManagementLevels((team) => [...team, option]);
    }
  };

  const handleClearFilters = (): void => {
    setActive('');
    setSelectedRoles([]);
    setSelectedTeams([]);
    setSelectedRegions([]);
    setSelectedManagementLevels([]);
  };

  const handleClearUsers = (): void => {
    dispatch(selectUsers([]));
  };

  const noFilters =
    !active &&
    selectedRoles.length === 0 &&
    selectedTeams.length === 0 &&
    selectedRegions.length === 0 &&
    selectedManagementLevels.length === 0;

  return (
    <div className="flex flex-row gap-2 justify-between w-full">
      <div className="flex flex-row gap-2 w-full">
        <div className="w-72">
          <UserPermissionsSelectFilter />
        </div>

        <div className="w-30">
          <MultiselectDropdownRedesign
            placeholder="Roles"
            onChange={handleChangeRoles}
            options={rolesOptions}
            selectedItems={selectedRoles}
            dataTestid="active"
          />
        </div>
        <div className="w-30">
          <MultiselectDropdownRedesign
            placeholder="Division"
            onChange={handleChangeTeams}
            options={teamOptions}
            selectedItems={selectedTeams}
            dataTestid="active"
          />
        </div>
        <div className="w-30">
          <MultiselectDropdownRedesign
            placeholder="Region"
            onChange={handleChangeRegions}
            options={regionsOptions}
            selectedItems={selectedRegions}
            dataTestid="active"
          />
        </div>
        {!userTypeName && (
          <div className="w-30">
            <MultiselectDropdownRedesign
              placeholder="Level"
              onChange={handleChangeManagementLevels}
              options={managementLevelsOptions}
              selectedItems={selectedManagementLevels}
              dataTestid="active"
            />
          </div>
        )}
        {!userTypeName && (
          <div className="w-32">
            <SelectRedesign
              placeholder="Integrations"
              onChange={handleChangeActive}
              options={activeOptions}
              value={active}
            />
          </div>
        )}
        <div className="flex flex-row items-end">
          <button
            type="button"
            className={`whitespace-no-wrap text-litlingo-primary underline focus:outline-none ${
              noFilters ? 'text-litlingo-gray-3' : ''
            }`}
            onClick={handleClearFilters}
            disabled={noFilters}
          >
            Clear All
          </button>
        </div>
      </div>
      <button
        type="button"
        className={`whitespace-no-wrap text-litlingo-primary underline focus:outline-none ${
          selectedUsers.length === 0 ? 'text-litlingo-gray-3' : ''
        }`}
        onClick={handleClearUsers}
        disabled={selectedUsers.length === 0}
      >
        Clear selections
      </button>
    </div>
  );
};

export default UserPermissionsFilters;
